import { Box, Button, Grid, IconButton, MenuItem, TextField, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { ArrowBack, Save } from '@material-ui/icons';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CurrencyInput } from '../../components/CurrencyInput';
import Logger from '../../components/Logger';
import Page from '../../containers/Page/Page';
import { api } from '../../services/api';
import { ILog } from '../../types/Logger';
import { Helpers } from '../../utils/Helpers';

interface ICPP {
    numcpp: string;
    desc_cpp: string;
    moeda: string;
    valor_unit_cpp: string;
    fat_mg2: string;
    fat_gross: string;
    cpp_porcentagem: string;
    valido_de: string;
    valido_ate: string;
}

interface IParams {
    id: string;
}

function CPPCreate() {

    //Hooks
    const history = useHistory();
    const classes = useStyles();
    const params = useParams<IParams>();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //States
    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [cpp, setCPP] = React.useState<ICPP>({} as ICPP);
    const [logs, setLogs] = React.useState<ILog[]>([]);

    //Effects
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setCPP({
            ...cpp,

        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carregar CPP.
     * @returns void
     */
    async function handleFetch() {
        setLoading(true);
        if (params.id) {
            try {
                const response = await api.get(`cpp/${params.id}`);
                setCPP(response.data.object);
                setLogs(response.data.object.logs);
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            }
        } else {
            try {
                const response = await api.get('cpp/create');
                setCPP({
                    ...cpp,
                    numcpp: response.data.object,
                    cpp_porcentagem: '100',
                    valido_de: moment().format('YYYY-MM-DD'),
                    valido_ate: moment().format('YYYY-MM-DD'),
                });
                setError('');
            } catch (error: any) {
                setError(Helpers.errorFormat(error));
            }
        }
        setLoading(false);
    }

    /**
     * Salvar CPP.
     * @returns void
     */
    async function handleSave() {
        setLoading(true);
        try {
            const response = await api.request({
                url: params.id ? `cpp/${params.id}` : 'cpp',
                method: params.id ? 'PUT' : 'POST',
                data: { ...cpp },
            });
            alert(response.data.message, { variant: 'success' });
            if (!params.id) { history.push(`/cpp/${response.data.object.id}`); }
        } catch (error) {
            Helpers.error(error, alert);
        }
        setLoading(false);
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.goBack()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {params.id ? 'Editar CPP' : 'Novo CPP'}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {params.id &&
                        <Box mr={2}>
                            <Logger
                                logs={logs}
                            />
                        </Box>
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSave()}>
                        Salvar
                    </Button>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="numcpp"
                                    name="numcpp"
                                    label="Número CPP"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={cpp.numcpp || ''}
                                    onChange={(event) => setCPP({ ...cpp, numcpp: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="desc_cpp"
                                    name="desc_cpp"
                                    label="Desc. CPP"
                                    variant="outlined"
                                    fullWidth={true}
                                    value={cpp.desc_cpp || ''}
                                    onChange={(event) => setCPP({ ...cpp, desc_cpp: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="moeda"
                                    name="moeda"
                                    label="Moeda"
                                    size="small"
                                    variant="outlined"
                                    fullWidth={true}
                                    select={true}
                                    value={cpp.moeda || ''}
                                    onChange={(event) => setCPP({ ...cpp, moeda: event.target.value })}
                                >
                                    <MenuItem value="R$">R$</MenuItem>
                                    <MenuItem value="USD">USD</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CurrencyInput
                                    hideSymbol={true}
                                    value={Number.parseFloat(cpp.valor_unit_cpp) || 0}
                                    onChangeValue={(_e, ov) => setCPP({ ...cpp, valor_unit_cpp: ov.toString() })}
                                    InputElement={
                                        <TextField
                                            id="valor_unit_cpp"
                                            name="valor_unit_cpp"
                                            label="Valor Unit. CPP"
                                            variant="outlined"
                                            fullWidth={true}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="fat_mg2"
                                    name="fat_mg2"
                                    label="Fat. MG2"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    value={cpp.fat_mg2 || ''}
                                    onChange={(event) => setCPP({ ...cpp, fat_mg2: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="fat_gross"
                                    name="fat_gross"
                                    label="Fat. Gross"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    value={cpp.fat_gross || ''}
                                    onChange={(event) => setCPP({ ...cpp, fat_gross: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="cpp_porcentagem"
                                    name="cpp_porcentagem"
                                    label="Porcentagem KSB (%)"
                                    variant="outlined"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0, step: 'any' } }}
                                    fullWidth={true}
                                    value={cpp.cpp_porcentagem || ''}
                                    onChange={(event) => setCPP({ ...cpp, cpp_porcentagem: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="valido_de"
                                    name="valido_de"
                                    label="Válido De"
                                    variant="outlined"
                                    type="date"
                                    fullWidth={true}
                                    value={cpp.valido_de || ''}
                                    onChange={(event) => setCPP({ ...cpp, valido_de: event.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    id="valido_ate"
                                    name="valido_ate"
                                    label="Válido Até"
                                    variant="outlined"
                                    type="date"
                                    fullWidth={true}
                                    value={cpp.valido_ate || ''}
                                    onChange={(event) => setCPP({ ...cpp, valido_ate: event.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
    })
);

export default CPPCreate;